import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './RouteGuards';
import AppLayout from '@/components/layout/AppLayout';
import SuspendedAccountScreen from '@/components/auth/SuspendedAccountScreen';
import { useAuth } from '@/contexts/AuthContext';

const SuspendedAccountScreenWrapper = () => {
  const { profile } = useAuth();
  return (
    <SuspendedAccountScreen
      suspendedUntil={(profile as any)?.suspended_until}
      suspensionReason={(profile as any)?.suspension_reason}
    />
  );
};

// Import protected pages
import Dashboard from "@/pages/dashboard";
import EstimatesPage from "@/pages/estimates/index";
import CreateEstimate from "@/pages/estimates/create";
import EditEstimate from "@/pages/estimates/edit/[id]";
import EstimateDetails from "@/pages/estimates/[id]";
import EditEstimateItems from "@/pages/estimates/edit/[id]/items";
import CustomersPage from "@/pages/customers/index";
import CreateCustomer from "@/pages/customers/create";
import InvoicesPage from "@/pages/invoices/index";
import CreateInvoice from "@/pages/invoices/create";
import EditInvoice from "@/pages/invoices/edit/[id]";
import InvoiceDetails from "@/pages/invoices/[id]";
import ProjectsPage from "@/pages/projects/index";
import CreateProject from "@/pages/projects/create";
import EditProject from "@/pages/projects/edit/[id]";
import ProjectDetails from "@/pages/projects/[id]";
import ProfileSetup from "@/pages/ProfileSetup";
import CompanySettings from "@/pages/company/Settings";
import ProfileSettings from "@/pages/settings/Profile";
import TeamPage from "@/pages/people/Team";
import AdminPanel from "@/pages/admin/index";
import FeatureNotAvailable from "@/pages/FeatureNotAvailable";

// Cost Center Pages
import SuppliersIndex from "@/pages/cost-center/suppliers/index";
import CreateSupplier from "@/pages/cost-center/suppliers/create";
import EditSupplier from "@/pages/cost-center/suppliers/edit/[id]";
import InternalLineItemsIndex from "@/pages/cost-center/internal-line-items/index";
import CreateInternalLineItem from "@/pages/cost-center/internal-line-items/create";
import EditInternalLineItem from "@/pages/cost-center/internal-line-items/edit/[id]";
import CostCenterSettings from "@/pages/cost-center/settings/index";
import DocumentDefaults from "@/pages/cost-center/document-defaults/index";
import SupplierLineItemsPage from "@/pages/cost-center/suppliers/line-items/index";
import CreateSupplierLineItemPage from "@/pages/cost-center/suppliers/line-items/create";
import EditSupplierLineItemPage from "@/pages/cost-center/suppliers/line-items/edit/[id]";
import SupplierDetailsPage from "@/pages/cost-center/suppliers/[supplierId]/index";
import ViewSupplierDocument from "@/pages/documents/ViewSupplierDocument";
import NoCompany from "@/pages/account/NoCompany";

// Finance Pages
import CreditNotes from "@/pages/finances/CreditNotes";
import CreateCreditNote from "@/pages/finances/credit-notes/create";
import CreditDetails from "@/pages/credits/CreditDetails";

import CompanyCreationPage from "@/pages/company-creation/CompanyCreationPage";
import DeactivatedPage from "@/pages/deactivated";
import CompanySuspendedScreen from "@/components/auth/CompanySuspendedScreen";
import ResetPassword from "@/pages/ResetPassword";
import EmailChangeSuccess from "@/pages/EmailChangeSuccess";



export function ProtectedRoutes() {
  console.log('[ProtectedRoutes] Rendering protected routes - EXCLUDES /join');
  
  try {
    return (
      <Routes>
        {/* Profile Setup - No Layout Wrapper */}
        <Route
          path="/profile-setup"
          element={
            <ProtectedRoute>
              <ProfileSetup />
            </ProtectedRoute>
          }
        />

        {/* Company Creation - No Layout Wrapper */}
        <Route
          path="/company-creation"
          element={
            <ProtectedRoute>
              <CompanyCreationPage />
            </ProtectedRoute>
          }
        />

        {/* No Company - No Layout Wrapper */}
        <Route
          path="/account/no-company"
          element={
            <ProtectedRoute>
              <NoCompany />
            </ProtectedRoute>
          }
        />

        {/* Suspended - No Layout Wrapper, no ProtectedRoute to avoid redirect loops */}
        <Route
          path="/suspended"
          element={<SuspendedAccountScreenWrapper />}
        />

        <Route path="/reset-password" element={<ResetPassword />} />

        <Route path="/email-change-success" element={<EmailChangeSuccess />} />

        {/* Deactivated - No Layout Wrapper */}
        <Route
          path="/deactivated"
          element={
            <ProtectedRoute>
              <DeactivatedPage />
            </ProtectedRoute>
          }
        />

        {/* Company Suspended - No Layout Wrapper */}
        <Route
          path="/company-suspended/:companyId"
          element={
            <ProtectedRoute>
              <CompanySuspendedScreen />
            </ProtectedRoute>
          }
        />

        {/* All Main App Routes - With Unified Layout */}
        <Route path="/*" element={
          <ProtectedRoute>
            <AppLayout>
              <Routes>
                {/* Dashboard */}
                <Route path="/dashboard" element={<Dashboard />} />
                
                {/* ==================== SALES ==================== */}
                <Route path="/estimates" element={<EstimatesPage />} />
                <Route path="/estimates/create" element={<CreateEstimate />} />
                <Route path="/estimates/edit/:id" element={<EditEstimate />} />
                <Route path="/estimates/:id" element={<EstimateDetails />} />
                <Route path="/estimates/edit/:id/items" element={<EditEstimateItems />} />
                <Route path="/invoices" element={<InvoicesPage />} />
                <Route path="/invoices/create" element={<CreateInvoice />} />
                <Route path="/invoices/edit/:id" element={<EditInvoice />} />
                <Route path="/invoices/:id" element={<InvoiceDetails />} />
                <Route path="/sales/proposals" element={<FeatureNotAvailable />} />
                
                {/* ==================== COST CENTER ==================== */}
                <Route path="/cost-center" element={<Navigate to="/cost-center/internal-line-items" replace />} />
                <Route path="/cost-center/assemblies" element={<FeatureNotAvailable />} />
                <Route path="/cost-center/assemblies/create" element={<FeatureNotAvailable />} />
                <Route path="/cost-center/assemblies/edit/:id" element={<FeatureNotAvailable />} />
                <Route path="/cost-center/internal-line-items" element={<InternalLineItemsIndex />} />
                <Route path="/cost-center/internal-line-items/create" element={<CreateInternalLineItem />} />
                <Route path="/cost-center/internal-line-items/edit/:id" element={<EditInternalLineItem />} />
                <Route path="/cost-center/overheads" element={<FeatureNotAvailable />} />
                <Route path="/cost-center/settings" element={<CostCenterSettings />} />
                <Route path="/cost-center/document-defaults" element={<DocumentDefaults />} />
                <Route path="/cost-center/suppliers" element={<SuppliersIndex />} />
                <Route path="/cost-center/suppliers/create" element={<CreateSupplier />} />
                <Route path="/cost-center/suppliers/edit/:id" element={<EditSupplier />} />
                <Route path="/cost-center/suppliers/:supplierId" element={<SupplierDetailsPage />} />
                <Route path="/cost-center/suppliers/:supplierId/line-items" element={<SupplierLineItemsPage />} />
                <Route path="/cost-center/suppliers/:supplierId/line-items/create" element={<CreateSupplierLineItemPage />} />
                <Route path="/cost-center/suppliers/:supplierId/line-items/edit/:id" element={<EditSupplierLineItemPage />} />
                <Route path="/documents/supplier/:documentId" element={<ViewSupplierDocument />} />
                
                {/* ==================== PROJECTS & SCHEDULING ==================== */}
                <Route path="/projects" element={<ProjectsPage />} />
                <Route path="/projects/create" element={<CreateProject />} />
                <Route path="/projects/edit/:id" element={<EditProject />} />
                <Route path="/projects/:id" element={<ProjectDetails />} />
                <Route path="/scheduling/calendar" element={<FeatureNotAvailable />} />
                <Route path="/scheduling/appointments" element={<FeatureNotAvailable />} />
                <Route path="/scheduling/maintenance" element={<FeatureNotAvailable />} />
                <Route path="/scheduling/tasks" element={<FeatureNotAvailable />} />
                
                {/* ==================== PEOPLE ==================== */}
                <Route path="/customers" element={<CustomersPage />} />
                <Route path="/customers/new" element={<CreateCustomer />} />
                <Route path="/people/team" element={<TeamPage />} />
                
                {/* ==================== FLEET & EQUIPMENT ==================== */}
                <Route path="/fleet/vehicles" element={<FeatureNotAvailable />} />
                <Route path="/fleet/equipment" element={<FeatureNotAvailable />} />
                <Route path="/fleet/gps" element={<FeatureNotAvailable />} />
                
                {/* ==================== WORKFORCE ==================== */}
                <Route path="/workforce/time-tracking" element={<FeatureNotAvailable />} />
                <Route path="/workforce/time-cards" element={<FeatureNotAvailable />} />
                <Route path="/workforce/crew-scheduling" element={<FeatureNotAvailable />} />
                
                {/* ==================== COMMUNICATION ==================== */}
                <Route path="/communication/internal" element={<FeatureNotAvailable />} />
                <Route path="/communication/client-portal" element={<FeatureNotAvailable />} />
                
                {/* ==================== GROWTH CENTER ==================== */}
                <Route path="/growth/leads" element={<FeatureNotAvailable />} />
                <Route path="/growth/campaigns" element={<FeatureNotAvailable />} />
                <Route path="/growth/audiences" element={<FeatureNotAvailable />} />
                <Route path="/growth/forms" element={<FeatureNotAvailable />} />
                <Route path="/growth/automations" element={<FeatureNotAvailable />} />
                
                {/* ==================== FINANCES ==================== */}
                <Route path="/finances/credit-notes" element={<CreditNotes />} />
                <Route path="/finances/credit-notes/create" element={<CreateCreditNote />} />
                <Route path="/credits/:id" element={<CreditDetails />} />
                <Route path="/finances/budgets" element={<FeatureNotAvailable />} />
                <Route path="/finances/payments" element={<FeatureNotAvailable />} />
                <Route path="/finances/reports" element={<FeatureNotAvailable />} />
                
                {/* ==================== SETTINGS ==================== */}
                <Route path="/settings" element={<CompanySettings />} />
                <Route path="/settings/profile" element={<ProfileSettings />} />
                <Route path="/settings/compliance" element={<FeatureNotAvailable />} />
                <Route path="/settings/integrations" element={<FeatureNotAvailable />} />
                <Route path="/settings/notifications" element={<FeatureNotAvailable />} />
                
                {/* ==================== ADMIN ==================== */}
                <Route path="/admin" element={<AdminPanel />} />
                
                {/* ==================== LEGACY REDIRECTS ==================== */}
                <Route path="/leads" element={<Navigate to="/growth/leads" replace />} />
                <Route path="/team" element={<Navigate to="/people/team" replace />} />
                <Route path="/reports" element={<Navigate to="/finances/reports" replace />} />
                <Route path="/payments" element={<Navigate to="/finances/payments" replace />} />
                <Route path="/projects/expenses" element={<Navigate to="/finances/budgets" replace />} />
                
                {/* Default redirect */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </AppLayout>
          </ProtectedRoute>
        } />
      </Routes>
    );
  } catch (error) {
    console.error('[ProtectedRoutes] Error rendering routes:', error);
    return (
      <div className="min-h-screen bg-red-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="text-xl font-bold text-red-600">Protected Routes Error</h1>
          <p className="text-red-500">Check console for details</p>
        </div>
      </div>
    );
  }
}
